// File: properties/CheckChannelConsentProperties.jsx
import React from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  Select,
  Button,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import useConsentChannels from '../utils/useConsentChannels';

export default function CheckChannelConsentProperties({
  nodeConfig,
  setNodeConfig,
  updateNodeData,
}) {
  const { channels, loading: channelsLoading } = useConsentChannels();

  // Stored alongside the id so the canvas node can label itself without the list.
  const handleChannelChange = (id) => {
    const picked = channels.find((c) => String(c.id) === String(id));
    setNodeConfig({ ...nodeConfig, channel: id, channelLabel: picked?.label || '' });
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Step Name */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1.5}>
          Step Name
        </Text>
        <Input
          size="sm"
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., Is SMS Opt-In Active?"
        />
      </Box>

      <Divider />

      {/* Channel */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1.5}>
          Channel to Check
        </Text>
        <Select
          size="sm"
          value={nodeConfig.channel || ''}
          onChange={(e) => handleChannelChange(e.target.value)}
          placeholder={channelsLoading ? 'Loading channels…' : 'Select channel'}
          isDisabled={channelsLoading}
        >
          {channels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              {channel.label}
            </option>
          ))}
        </Select>
      </Box>

      <Divider />

      {/* Branch Labels */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Output Branch Labels
        </Text>
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="10px" fontWeight="semibold" color="green.700" mb={1}>
              ✓ Enabled path label
            </Text>
            <Input
              size="sm"
              value={nodeConfig.enabledLabel || ''}
              onChange={(e) =>
                setNodeConfig({ ...nodeConfig, enabledLabel: e.target.value })
              }
              placeholder="Enabled"
            />
          </Box>
          <Box>
            <Text fontSize="10px" fontWeight="semibold" color="red.600" mb={1}>
              ✗ Disabled path label
            </Text>
            <Input
              size="sm"
              value={nodeConfig.disabledLabel || ''}
              onChange={(e) =>
                setNodeConfig({ ...nodeConfig, disabledLabel: e.target.value })
              }
              placeholder="Disabled"
            />
          </Box>
        </VStack>
      </Box>

      {/* Info */}
      <Alert status="info" borderRadius="md" py={2} px={3}>
        <AlertIcon boxSize="14px" />
        <AlertDescription fontSize="xs">
          Reads the contact's current opt-in status for the selected channel and routes to
          the <strong>Enabled</strong> branch if opted in, or the <strong>Disabled</strong>{' '}
          branch if opted out or not set.
        </AlertDescription>
      </Alert>

      {/* Save */}
      <Button
        size="sm"
        w="full"
        bg="#7C3AED"
        color="white"
        _hover={{ bg: '#6D28D9' }}
        onClick={updateNodeData}
      >
        Update Node
      </Button>
    </VStack>
  );
}
